import * as Accordion from "#root/components/accordion";
import { Case, Switch } from "#root/components/conditionals";
import * as Fields from "#root/components/form/fields";
import { GlyphLoading } from "#root/components/glyphs";
import Sidebar from "#root/components/sidebar";
import StandardEditor, { type StandardEditorProps } from "#root/components/standard-editor";
import { WithSidebar } from "#root/components/withSidebar";
import {
  type EntitlementDetailItem,
  type EntitlementDetailItemSchema,
  type EntitlementListItemSchema,
  getEntitlementsByDeploymentCollection,
} from "#root/schema/entitlements";
import { productCollection } from "#root/schema/product";
import { useData } from "#root/utility/hooks";
import { leftJoin } from "#root/utility/joins";
import { mergeUpdate } from "#root/utility/merge-update";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import { flatToNamedTree } from "#root/utility/tree";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageLayout from "./layout";

export default function AdminEntitlementsPage({
                                                accountId,
                                                deploymentId,
                                                entitlementId,
                                              }: {
  accountId: string;
  deploymentId: string;
  entitlementId: string;
}): ReactNode {
  const navigate = useNavigate();
  const collection = useMemo(() => getEntitlementsByDeploymentCollection(accountId, deploymentId), [accountId, deploymentId]);

  const [entitlements, setEntitlements] = useData(async () => {
    const { rows } = await collection.list();
    return rows;
  }, [deploymentId]);

  const [products] = useData(async () => {
    const { rows } = await productCollection.list();
    return rows;
  });

  const initialData = useMemo(() => ({ deploymentId }), [deploymentId]);

  useMemo(
    () =>
      leftJoin({
        left: entitlements,
        right: products,
        leftKey: "productId",
        rightKey: "id",
        leftDest: "product",
      }),
    [entitlements, products],
  );

  const handleUpdate = useCallback(
    (update: RecordModificationResponse<EntitlementDetailItem>) => {
      setEntitlements(mergeUpdate(entitlements!, update));

      if ("deleted" in update) {
        navigate(`/admin/account/${accountId}/deployment/${deploymentId}/entitlement`);
      } else if ("created" in update) {
        navigate(`/admin/account/${accountId}/deployment/${deploymentId}/entitlement/${update.created}`);
      }
    },
    [navigate, setEntitlements, entitlements, accountId, deploymentId],
  );

  if (!entitlements || !products) {
    return (
      <PageLayout title="Entitlements">
        <section>
          <GlyphLoading/>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Admin - Entitlements">
      <WithSidebar>
        <EntitlementSidebar id={entitlementId} accountId={accountId} deploymentId={deploymentId} entitlements={entitlements}/>
        <section>
          <Switch>
            <Case expr={entitlementId == null}>
              <h1>Manage Entitlements</h1>
            </Case>
            <Case expr={entitlementId === "new"}>
              <h1>New Entitlement</h1>
              <Editor collection={collection} initialData={initialData} onSave={handleUpdate}/>
            </Case>
            <Case expr={entitlementId != null}>
              <h1>Edit Entitlement</h1>
              <Editor id={entitlementId} collection={collection} onSave={handleUpdate} onDelete={handleUpdate}/>
            </Case>
          </Switch>
        </section>
      </WithSidebar>
    </PageLayout>
  );
}

function EntitlementSidebar({
                              accountId,
                              deploymentId,
                              id,
                              entitlements,
                            }: {
  accountId: string;
  deploymentId: string;
  id: string | null;
  entitlements: EntitlementDetailItem[];
}) {
  const navigate = useNavigate();
  const menuSections = useMemo(() => flatToNamedTree(entitlements, e => e.product.type, e => e.product.name), [entitlements]);

  return (
    <Sidebar>
      <button className="flat center" type="button" onClick={() => navigate(`/admin/account/${accountId}/deployment/${deploymentId}/entitlement/new`)}>
        New Entitlement
      </button>
      <Accordion.Parent initialItemId={id}>
        {menuSections.map(({ name, items }) => (
          <Accordion.Group key={name} label={name}>
            {items.map(entitlement => (
              <Accordion.Item key={entitlement.id} itemId={entitlement.id}>
                <Link to={`/admin/account/${accountId}/deployment/${deploymentId}/entitlement/${entitlement.id}`}>{entitlement?.product?.name}</Link>
              </Accordion.Item>
            ))}
          </Accordion.Group>
        ))}
      </Accordion.Parent>
    </Sidebar>
  );
}

function Editor(props: StandardEditorProps<typeof EntitlementListItemSchema, typeof EntitlementDetailItemSchema>) {
  return (
    <StandardEditor {...props}>
      <Fields.Set name="productId">
        <label>Product</label>
        <Fields.AutoComplete required collection={productCollection}/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="options">
        <label>Options</label>
        <Fields.JSONEditor/>
        <Fields.Validity/>
      </Fields.Set>
    </StandardEditor>
  );
}
